import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, createElementVNode as _createElementVNode, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, Teleport as _Teleport, createBlock as _createBlock, createTextVNode as _createTextVNode, normalizeClass as _normalizeClass } from "vue"

const _hoisted_1 = {
  key: 0,
  class: "fixed bottom-[94px] right-[26px] bg-base-100 rounded-box z-[9999]"
}

import { onMounted, ref } from "vue";
import { useShepherdTourSteps } from "@/composables";
import { TourStep } from "@/composables/shepherd";
import { useHelpStore, AreaType } from "@/stores/help";
import Tour from "shepherd.js/src/types/tour";
import { LedRule } from "@/keyboard";
import { LocalStorage } from "@/localStorage";

type OnEnterLeave = Exclude<TourStep["onEnterLeave"], undefined>;
const guideDisabledStorageName = `guide-disabled_app-v2`;
const AUTOSTART = false;


export default /*@__PURE__*/_defineComponent({
  __name: 'HelpGuide',
  setup(__props) {

const help = useHelpStore();
help.reset();

const activeTour = ref<null | Tour>(null);

// eslint-disable-next-line
const noop = () => {};

const onNextButton = () => activeTour.value?.next();
const onBackButton = () => {
  const tour = activeTour.value;
  if (!tour) return;
  if (tour.getCurrentStep()?.id == tour.steps[0].id) tour.cancel();
  else tour.back();
};

const compose = (
  step: TourStep,
  { onEnter, onLeave }: OnEnterLeave
): TourStep => {
  const oldOnEnter = step.onEnterLeave?.onEnter ?? noop;
  const oldOnLeave = step.onEnterLeave?.onLeave ?? noop;
  return {
    ...step,
    onEnterLeave: {
      onEnter: () => {
        onEnter();
        return oldOnEnter();
      },
      onLeave: () => {
        onLeave();
        return oldOnLeave();
      },
    },
  };
};

const inArea = (step: TourStep, area: AreaType) =>
  compose(step, {
    onEnter: () => (help.selectedArea = area),
    onLeave: () => (help.selectedArea = null),
  });

const link = (href: string, text: string) =>
  `<a href="${href}" target="_blank" class="link">${text}</a>`;

const links = {
  configurator: link("/doc/configurator", "Documentation"),
  keys_configuration: link("/doc/keys-configuration", "Keys configuration"),
  managing_configuration: link(
    "/doc/configurator#managing-your-configurations",
    "Managing your configurations"
  ),
  flashing: link("/doc/configurator#flashing", "Flashing"),
  leds_configuration: link("/doc/leds-configuration", "LEDs configuration"),
  mouse_emulation: link("/doc/configurator#mouse-emulation", "Mouse emulation"),
};

const stepText = (...parts: string[]) => {
  const newParts = ["<p>"];
  for (const part of parts) {
    if (part == "") newParts.push("</p>", "<p>");
    else newParts.push(part);
  }
  newParts.push("</p>");
  return newParts
    .join(" ")
    .replaceAll(/ +/g, " ") // squeeze multiple spaces
    .replaceAll(/(^|\s)-(\s|$)/g, "$1—$2") // replace hyphens with emdash
    .replaceAll(/`([^`]+)`/g, "<strong>$1</strong>");
};

const ledOverrides: LedRule[] = [
  {
    condition: "Always",
    pattern: {
      phase: { x: 0, y: 0 },
      repeat: "Wrap",
      transitions: [
        { color: [0, 0, 0], duration: 1000, interpolation: "Linear" },
        { color: [255, 200, 0], duration: 1000, interpolation: "Linear" },
        { color: [255, 200, 0], duration: 1000, interpolation: "Linear" },
        { color: [0, 0, 0], duration: 1000, interpolation: "Linear" },
      ],
    },
  },
  {
    condition: "Pressed",
    pattern: {
      phase: { x: 0, y: 0 },
      repeat: "Once",
      transitions: [
        { color: [255, 255, 255], duration: 200, interpolation: "Linear" },
        { color: [255, 255, 255], duration: 200, interpolation: "Linear" },
        { color: [0, 0, 0], duration: 200, interpolation: "Linear" },
      ],
    },
  },
];

const areas: Record<AreaType, TourStep[]> = {
  keys: [
    {
      element: ".tour-help",
      title: "Keyboard configurator guide",
      text: stepText(
        "This guide will give you basic information on how to use the keyboard configurator.",
        `Whenever you feel that you need more details visit the ${links.configurator}.`,
        "",
        "Use `arrow keys` or click `NEXT` to continue."
      ),
    },
    {
      element: ".tour-areas",
      on: "bottom",
      title: "Configurator views",
      text: stepText(
        "The configurator consists of several different views.",
        "You can switch between views using these buttons.",
        "",
        "The rest of this guide describes the `KEYS` view.",
        "To get help for another view, select it and click the `?` button."
      ),
    },

    {
      element: ".tour-key-area",
      on: "bottom",
      text: "Here you define functions of keys on the current layer.",
    },
    {
      element: ".tour-layer-switcher",
      on: "top",
      text: stepText(
        "To switch between layers click on corresponding layer number.",
        "You can add new layers using the `+` button or delete existing ones by hovering and clicking the delete icon."
      ),
    },

    {
      element: ".tour-key-cap",
      on: "auto",
      text: "Click on a key to customize its functions.",
      onEnterLeave: {
        onEnter: () => help.selectKey(1, 4),
        onLeave: () => help.deselectKey(),
      },
    },
    {
      element: ".tour-key-menu",
      on: "auto",
      text: "Here you can set the key codes that will be sent when the key is pressed or assign special actions.",
      onEnterLeave: {
        onEnter: () =>
          help.selectKey(1, 4, { expand: true, action: { KeyCode: "R" } }),
        onLeave: () => help.deselectKey(),
      },
    },
    {
      element: ".tour-key-menu",
      on: "auto",
      text: stepText(
        "You can define multiple key codes for a single key and/or use special actions, like switching layers, mouse emulation and more.",
        "",
        `See ${links.keys_configuration} for the description of available actions.`
      ),
      onEnterLeave: {
        onEnter: () =>
          help.selectKey(1, 4, {
            expand: true,
            action: {
              MultipleActions: [
                { MultipleKeyCodes: ["LCtrl", "LShift", "R"] },
                { Layer: 2 },
                { Custom: { Mouse: { Click: "Left" } } },
              ],
            },
          }),
        onLeave: () => help.deselectKey(),
      },
    },

    {
      element: ".tour-config-info",
      on: "bottom",
      text: stepText(
        "This area allows to switch between existing keyboard configurations or to save/modify the current one (if you're logged in).",
        `See ${links.managing_configuration} for more information.`
      ),
    },
    {
      element: ".tour-compile-btn",
      on: "left",
      text: "Click this button to generate firmware for the current configuration. This operation might take a while.",
    },
    {
      element: ".tour-firmware-btns",
      on: "left",
      text: stepText(
        "When firmware compilation has been finished, you can download the firmware <i>.bin</i> file using the `DOWNLOAD` button",
        "or can directly flash the firmware using the `FLASH` button if your browser supports it.",
        "Otherwise use the `inscribe-flasher` desktop app.",
        `See ${links.flashing} for more information.`
      ),
    },
  ],

  leds: [
    {
      title: "LEDs configuration",
      text: stepText(
        "Here you can configure RGB LED lighting of your keyboard - from basic colors to complex animations.",
        "",
        `This guide should give you basic understanding, for detailed information see ${links.leds_configuration}.`
      ),
      onEnterLeave: {
        onEnter: () => help.useLedRules({ rules: ledOverrides }),
        onLeave: () => help.clearLedRules(),
      },
    },
    {
      element: ".tour-led-configs",
      on: "bottom",
      text: stepText(
        "Here you can select currently edited configuration.",
        "",
        "Only one keyboard LED configuration is enabled at time and you can switch between them using `Led Cycle Up`/`Led Cycle Down` actions.",
        "",
        `See ${links.leds_configuration} for more information.`
      ),
      onEnterLeave: {
        onEnter: () => help.useLedRules({ rules: ledOverrides }),
        onLeave: () => help.clearLedRules(),
      },
    },
    {
      element: ".tour-led-rule",
      on: "auto",
      text: stepText(
        'Each of these boxes represents a single "LED rule".',
        "You can add, delete and drag&drop rules to change their order.",
        "Rules at the bottom have higher priority.",
        "",
        "Click on a rule to edit it."
      ),
      onEnterLeave: {
        onEnter: () => help.useLedRules({ rules: ledOverrides, select: 0 }),
        onLeave: () => help.clearLedRules(),
      },
    },
    {
      element: ".tour-led-rule",
      on: "auto",
      text: stepText(
        "You can decide which keyboard keys are affected by the rule, under what condition the rule is enabled",
        "and what color pattern will be displayed on matched keys' LEDs when the rule is active.",
        "",
        `For more information see ${links.leds_configuration}.`
      ),
      onEnterLeave: {
        onEnter: () =>
          help.useLedRules({ rules: ledOverrides, select: 0, expand: true }),
        onLeave: () => help.clearLedRules(),
      },
    },
  ],

  mouse: [
    {
      title: "Mouse emulation settings",
      text: stepText(
        "This area allows to configure mouse emulation settings,",
        "you can ignore this if you don't use mouse emulation features.",
        "The defaults should work well for most users but you are free to experiment."
      ),
    },
    {
      element: ".tour-mouse-config",
      on: "auto",
      text: `You can change speed profile for each mouse axis separately. All the parameters have been explained in ${links.mouse_emulation}.`,
    },
    {
      element: ".tour-mouse-plot-btn",
      on: "auto",
      text: "Click here to show a plot of the speed profile.",
    },
    {
      element: ".tour-mouse-plot",
      on: "auto",
      text: stepText(
        `You can change speed profile for each mouse axis separately. All the parameters have been explained in ${links.mouse_emulation}.`
      ),
      onEnterLeave: {
        onEnter: () => (help.showMousePlot = true),
        onLeave: () => (help.showMousePlot = false),
      },
    },
  ],

  general: [
    {
      title: "General settings",
      text: "Here you can configure some general keyboard settings not available in other views.",
    },
    {
      element: ".tour-general-hold-tap",
      on: "auto",
      text: "These are the global Hold/Tap settings that will be used for key's Hold/Tap action if no overrides have been set for this key.",
    },
    {
      element: ".tour-general-firmware",
      on: "auto",
      text: "These settings allow you to configure internals of your keybooard. Use them only if you know what you're doing.",
    },
  ],

  keycaps: [
    {
      title: "Keycaps colors",
      text: stepText(
        "This view allows you to configure the colors of keycaps in the web interface. This does not affect the keyboard firmware.",
        "",
        "Click on the keys to select/deselect them (or use `ALL`/`CLEAR`/`LEFT`/`RIGHT`).",
        "Then pick a color to use for selected keys."
      ),
    },
  ],

  detect: [
    {
      title: "Firmware detection",
      text: stepText(
        "This view allows to detect keyboard configuration from an existing firmware file.",
        "",
        "Detection works only for firmware built from this website.",
        "You will only be able to see public configurations or the ones you own (if you are logged in).",
        "",
        "The detection result is just a guess and might not always be correct."
      ),
    },
    {
      element: ".tour-detect-input",
      on: "bottom",
      text: stepText(
        "You can provide either firmware file name or its Build ID.",
        "If you don't know the name you can provide file's SHA-256 hash."
      ),
    },
    {
      element: ".tour-detect-upload",
      on: "auto",
      text: "Use this button to calculate the SHA-256 hash automatically.",
    },
    {
      element: ".tour-detect-load",
      on: "auto",
      text: "When a configuration has been found, click this button to load it.",
    },
  ],
};

const makeTour = (area: AreaType, steps: TourStep[]) => {
  const tour = useShepherdTourSteps(steps.map((s) => inArea(s, area)));

  tour.on("start", () => (activeTour.value = tour));
  ["cancel", "complete"].forEach((e) =>
    tour.on(e, () => {
      activeTour.value = null;

      help.reset();
    })
  );

  return tour;
};

const tours = Object.fromEntries(
  (Object.entries(areas) as [AreaType, TourStep[]][]).map(([area, steps]) => [
    area,
    makeTour(area, steps),
  ])
) as Record<AreaType, Tour>;

const DEFAULT_TOUR = tours.keys;

const startGuide = () => {
  const tour = tours[help.currentGuideArea] ?? DEFAULT_TOUR;
  tour.start();
};

// Autostart
const guideDisabledStorage = new LocalStorage(
  guideDisabledStorageName,
  { type: "boolean" },
  (v: boolean) => v,
  (v: boolean) => v
);

const isDisabled = () => guideDisabledStorage.get() ?? false;
const setDisabled = () => guideDisabledStorage.set(true);

DEFAULT_TOUR.on("complete", setDisabled);
if (AUTOSTART && !isDisabled()) onMounted(DEFAULT_TOUR.start);

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", {
    class: _normalizeClass(["tour-help fixed z-10 bottom-5 right-5 btn btn-neutral btn-circle text-xl border border-base-100/50", !isDisabled() && !_unref(DEFAULT_TOUR).isActive() ? 'help-bounce' : '']),
    onClick: startGuide
  }, [
    _cache[0] || (_cache[0] = _createTextVNode(" ? ")),
    (_openBlock(), _createBlock(_Teleport, { to: "body" }, [
      (activeTour.value != null)
        ? (_openBlock(), _createElementBlock("div", _hoisted_1, [
            _createElementVNode("div", { class: "p-3 flex justify-center items-center gap-2" }, [
              _createElementVNode("div", {
                class: "btn btn-neutral btn-sm",
                onClick: onBackButton
              }, "Back"),
              _createElementVNode("div", {
                class: "btn btn-sm btn-accent",
                onClick: onNextButton
              }, "Next")
            ])
          ]))
        : _createCommentVNode("", true)
    ]))
  ], 2))
}
}

})